import { useState } from "react";
import { useFormikContext } from "formik";
import { loadRazorpay } from "../../../admin/ParentAdmin/Register/Payment/LoadRazorpay";
import toast from "react-hot-toast";
import "./OnboardingPaymentButtons.css";
import { useNavigate } from "react-router-dom";
import { clearStudentDraft } from "../../../../utils/studentDraftStorage";

/**
 * Payment buttons that appear in AddStudentPage (inside Formik context).
 *
 * Single-click flow: clicking a payment button triggers:
 * 1. Validate Formik form
 * 2. Call completeOnboarding to save kids
 * 3. Call create-subscription API with parent token
 * 4. Redirect to payment gateway / open Razorpay modal
 *
 * @param {Object} props
 * @param {string} props.currency - Plan currency ("INR" or "USD")
 * @param {string} props.currencySymbol - "₹" or "$"
 * @param {number} props.totalPrice - Total price to display on buttons
 * @param {Object} props.plan - The plan object (needs plan.id)
 * @param {Function} props.onSaveKids - Async callback: (values) => Promise<{students}|null>
 *   Must validate, call completeOnboarding, and return the result.
 *   Returns null if validation or API failed.
 * @param {boolean} props.isDisabled - Whether buttons should be disabled
 */
export const OnboardingPaymentButtons = ({
  currency,
  currencySymbol,
  totalPrice,
  plan,
  billingPeriod = "/year",
  onSaveKids,
  isDisabled = false,
}) => {
  const { validateForm, setTouched, setSubmitting, values } =
    useFormikContext();

  const [processingMethod, setProcessingMethod] = useState(null);
  const isProcessing = Boolean(processingMethod);
  const setIsProcessing = (val) => {
    if (!val) setProcessingMethod(null);
  };

  // ── Coupon State ──
  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponSuccess, setCouponSuccess] = useState("");
  const [estimatedData, setEstimatedData] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const isYearly =
    !plan?.billing_cycle ||
    plan.billing_cycle.toLowerCase().includes("year");
  const billingCycleNote = isYearly ? "Billed annually" : "Billed monthly";

  /**
   * Fetch using a specific parent token (not from localStorage).
   */
  const fetchWithToken = async (url, token, options = {}) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };
    return fetch(url, { ...options, headers });
  };

  /**
   * Gets the parent token from localStorage.
   * @returns {string|null}
   */
  const getParentToken = () => {
    try {
      const parentLocalData = JSON.parse(
        localStorage.getItem("parentLocalData"),
      );
      return parentLocalData?.token || null;
    } catch {
      return null;
    }
  };

  // ── Coupon: Apply ──
  const handleApplyCoupon = async () => {
    const trimmed = coupon.trim();
    if (trimmed === "") return;

    const parentToken = getParentToken();
    if (!parentToken) {
      setCouponError("Unable to validate coupon. Please try again.");
      return;
    }

    setCouponLoading(true);
    setCouponError("");

    const number_of_kids =
      localStorage.getItem("number_of_kids") || String(values.kids.length);
    const student_ids = (localStorage.getItem("student_ids") || "")
      .split(",")
      .map((id) => Number(id.trim()))
      .filter((id) => !isNaN(id));

    try {
      const response = await fetchWithToken(
        `${apiUrl}/subscriptions/billing/estimate`,
        parentToken,
        {
          method: "POST",
          body: JSON.stringify({
            coupon_code: trimmed,
            quantity: number_of_kids,
            plan_id: plan?.id,
            mode: "create",
            student_ids,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        setAppliedCoupon(trimmed);
        setCouponSuccess("Coupon Applied Successfully");
        setEstimatedData(data);
        setIsCouponApplied(true);
      } else {
        setCouponError(data?.message || "Invalid coupon code");
      }
    } catch (err) {
      setCouponError("Error validating coupon");
    } finally {
      setCouponLoading(false);
    }
  };

  // ── Coupon: Remove ──
  const handleRemoveCoupon = async () => {
    setAppliedCoupon(null);
    setCoupon("");
    setCouponError("");
    setIsCouponApplied(false);
    setCouponSuccess("");

    const parentToken = getParentToken();
    if (!parentToken) return;

    const number_of_kids =
      localStorage.getItem("number_of_kids") || String(values.kids.length);
    const student_ids = (localStorage.getItem("student_ids") || "")
      .split(",")
      .map((id) => Number(id.trim()))
      .filter((id) => !isNaN(id));

    try {
      const estimateResponse = await fetchWithToken(
        `${apiUrl}/subscriptions/billing/estimate`,
        parentToken,
        {
          method: "POST",
          body: JSON.stringify({
            coupon_code: "",
            quantity: number_of_kids,
            plan_id: plan?.id,
            mode: "create",
            student_ids,
          }),
        },
      );

      const estimateResult = await estimateResponse.json();

      if (estimateResponse.ok) {
        setEstimatedData(estimateResult);
      }
    } catch (err) {
      // Silent — price falls back to client-side total
    }
  };

  /**
   * Validates the Formik form and scrolls to the first error if any.
   * @returns {boolean} true if form is valid
   */
  const validateAndTouch = async () => {
    const errors = await validateForm();
    const hasErrors = Object.keys(errors).length > 0;

    if (hasErrors) {
      const touchedFields = {};
      if (errors.kids) {
        touchedFields.kids = errors.kids.map((kidErrors) => {
          if (!kidErrors) return {};
          const touched = {};
          Object.keys(kidErrors).forEach((key) => {
            touched[key] = true;
          });
          return touched;
        });
      }
      setTouched(touchedFields);

      setTimeout(() => {
        const firstErrorEl = document.querySelector(".kidDetailsForm__error");
        if (firstErrorEl) {
          firstErrorEl.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return false;
    }

    return true;
  };

  /**
   * Full payment flow:
   * validate → save kids → switch to parent → create subscription → redirect
   */
  const handlePaymentClick = async (paymentMethod) => {
    // 1. Validate form
    const isValid = await validateAndTouch();
    if (!isValid) return;

    if (values.kids.length > 5) {
      toast.error("You can only have up to 5 students.");
      return;
    }

    setProcessingMethod(paymentMethod);
    setSubmitting(true);

    try {
      // 2. Save kids (onboarding API)
      const result = await onSaveKids(values);
      if (!result?.students) {
        setIsProcessing(false);
        setSubmitting(false);
        return;
      }

      // Clear local draft for this parent
      try {
        const parentLocalData = JSON.parse(
          localStorage.getItem("parentLocalData"),
        );
        if (parentLocalData?.user?.id) {
          clearStudentDraft(parentLocalData.user.id);
        }
      } catch {}

      // Store student IDs and kid count for payment
      const studentIds = result.students.map((s) => s.id).join(",") || "";
      localStorage.setItem("student_ids", studentIds);
      localStorage.setItem("number_of_kids", String(values.kids.length));

      // Store first student ID and skip flag for auto-switch on PaymentStatusPage
      const firstStudentId = result.students[0]?.id;
      if (firstStudentId) {
        localStorage.setItem(
          "onboarding_first_student_id",
          String(firstStudentId),
        );
      }
      const progressSkipped = values.progressAssignedTo == null;
      localStorage.setItem(
        "onboarding_progress_skipped",
        String(progressSkipped),
      );

      // 3. Get pre-fetched parent token from localStorage
      const parentToken = getParentToken();

      if (!parentToken) {
        toast.error("Failed to get parent account token.");
        setIsProcessing(false);
        setSubmitting(false);
        return;
      }
      // 4. Build subscription body
      const subscriptions = studentIds
        .split(",")
        .map((id) => id.trim())
        .map((id) => ({ student_id: id }));

      const bodyData = {
        plan_id: String(plan?.id),
        subscriptions,
        mode: "create",
        payment_method: paymentMethod,
        num_kids: String(values.kids.length),
      };

      // Include coupon_code if a coupon has been applied
      if (appliedCoupon && appliedCoupon.trim()) {
        bodyData.coupon_code = appliedCoupon.trim();
      }

      // 5. Call the appropriate payment API
      if (paymentMethod === "stripe") {
        await handleStripe(parentToken, bodyData);
      } else if (paymentMethod === "paypal") {
        await handlePaypal(parentToken, bodyData);
      } else if (paymentMethod === "razorpay") {
        await handleRazorpay(parentToken, bodyData);
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
      setIsProcessing(false);
      setSubmitting(false);
    }
  };

  // ── Stripe ──
  const handleStripe = async (token, bodyData) => {
    try {
      const response = await fetchWithToken(
        `${apiUrl}/stripe/create-subscription`,
        token,
        {
          method: "POST",
          body: JSON.stringify(bodyData),
        },
      );

      localStorage.setItem("payment_mode", "stripe");
      const result = await response.json();

      if (!response.ok) {
        toast.error(result.message || result.error);
        setIsProcessing(false);
        setSubmitting(false);
        return;
      }

      localStorage.setItem("mode", "create");
      window.location.href = result.approval_link;
    } catch (error) {
      toast.error("Error subscribing with Stripe. Please try again.");
      setIsProcessing(false);
      setSubmitting(false);
    }
  };

  // ── PayPal ──
  const handlePaypal = async (token, bodyData) => {
    try {
      const response = await fetchWithToken(
        `${apiUrl}/paypal/create-subscription`,
        token,
        {
          method: "POST",
          body: JSON.stringify(bodyData),
        },
      );

      const result = await response.json();
      localStorage.setItem("payment_mode", "paypal");

      if (!response.ok) {
        toast.error(result.message || result.error);
        setIsProcessing(false);
        setSubmitting(false);
        return;
      }

      localStorage.setItem("mode", "create");
      window.location.href = result.approval_link;
    } catch (error) {
      toast.error("Error subscribing with PayPal. Please try again.");
      setIsProcessing(false);
      setSubmitting(false);
    }
  };

  // ── Razorpay ──
  const handleRazorpay = async (token, bodyData) => {
    const isLoaded = await loadRazorpay();
    if (!isLoaded) {
      toast.error("Razorpay SDK failed to load. Check your connection.");
      setIsProcessing(false);
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetchWithToken(
        `${apiUrl}/razorpay/create-subscription`,
        token,
        {
          method: "POST",
          body: JSON.stringify(bodyData),
        },
      );

      const result = await response.json();
      localStorage.setItem("payment_mode", "razorpay");

      if (!response.ok) {
        toast.error(result.message || result.error);
        setIsProcessing(false);
        setSubmitting(false);
        return;
      }

      if (!result.subscription_id) {
        toast.error("Failed to create subscription");
        setIsProcessing(false);
        setSubmitting(false);
        return;
      }

      localStorage.setItem("mode", "create");

      const options = {
        key: process.env.REACT_APP_PUBLIC_RAZORPAY_TEST_KEY_ID,
        subscription_id: result.subscription_id,
        name: "Yuva Quest",
        description: "Subscription Payment",
        image:
          "https://d2lvj32mp5x83n.cloudfront.net/static/logo-YQ/YQLogoPlain.png",
        theme: { color: "#2d8cf0" },
        handler: async function (resp) {
          await fetchWithToken(`${apiUrl}/razorpay/payment-callback`, token, {
            method: "POST",
            body: JSON.stringify({
              subscription_id: resp?.razorpay_subscription_id,
            }),
          });

          navigate(
            `/razorpay/result/?subscription_id=${result.subscription_id}&status=success&type=add-student`,
          );
        },
        modal: {
          ondismiss: function () {
            // In case of closing the razorpay modal simply set is processing and submitting to false and don't need to redirect to any other page
            setIsProcessing(false);
            setSubmitting(false);
          },
        },
        prefill: {
          name: "Guest User",
          email: "test@example.com",
          contact: "9999999999",
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (resp) {
        setIsProcessing(false);
        setSubmitting(false);
        navigate(
          `/razorpay/result/?subscription_id=${result.subscription_id}&status=failed&type=add-student`,
        );
      });

      rzp.open();
      // Don't reset isProcessing/isSubmitting here — modal is still open.
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      setIsProcessing(false);
      setSubmitting(false);
    }
  };

  const disabled = isDisabled || isProcessing;

  // Determine the displayed price: use estimatedData if coupon applied, else client-side total
  const displayPrice =
    isCouponApplied && estimatedData
      ? Number(estimatedData.final_amount)
      : totalPrice;

  return (
    <div
      className={`onboardingPayment ${disabled ? "onboardingPayment--disabled" : ""}`}
    >
      <div className="onboardingPayment__card">
        {/* --- Header: Icon + Title + Amount --- */}
        <div className="onboardingPayment__header">
          <div className="onboardingPayment__headerLeft">
            <div className="onboardingPayment__iconWrapper">
              <svg
                className="onboardingPayment__walletIcon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="2"
                  y="6"
                  width="20"
                  height="14"
                  rx="3"
                  stroke="#3366FF"
                  strokeWidth="1.8"
                  fill="none"
                />
                <path
                  d="M6 6V5a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3v1"
                  stroke="#3366FF"
                  strokeWidth="1.8"
                  fill="none"
                />
                <circle cx="16.5" cy="13.5" r="1.5" fill="#3366FF" />
              </svg>
              {/* Decorative sparkles */}
              <span className="onboardingPayment__sparkle onboardingPayment__sparkle--1">
                ✦
              </span>
              <span className="onboardingPayment__sparkle onboardingPayment__sparkle--2">
                ✦
              </span>
              <span className="onboardingPayment__sparkle onboardingPayment__sparkle--3">
                ✦
              </span>
            </div>
            <div className="onboardingPayment__titleBlock">
              <h3 className="onboardingPayment__title">Payment Summary</h3>
            </div>
          </div>

          {totalPrice > 0 && (
            <div className="onboardingPayment__headerRight">
              <span className="onboardingPayment__amountLabel">
                Total Amount
              </span>
              {isCouponApplied && estimatedData ? (
                <>
                  <span className="onboardingPayment__amountOld">
                    {currencySymbol}
                    {Number(estimatedData.original_amount).toFixed(2)}
                  </span>
                  <span className="onboardingPayment__amount">
                    {currencySymbol}
                    {Number(displayPrice).toFixed(2)}
                    {billingPeriod && (
                      <span className="onboardingPayment__amountPeriod">
                        {billingPeriod}
                      </span>
                    )}
                  </span>
                </>
              ) : (
                <span className="onboardingPayment__amount">
                  {currencySymbol}
                  {Number(totalPrice).toFixed(2)}
                  {billingPeriod && (
                    <span className="onboardingPayment__amountPeriod">
                      {billingPeriod}
                    </span>
                  )}
                </span>
              )}
              <span className="onboardingPayment__billingNote">
                {billingCycleNote}
              </span>
            </div>
          )}
        </div>

        {/* --- Discount badge (when coupon applied) --- */}
        {isCouponApplied && estimatedData && (
          <div className="onboardingPayment__discountBadge">
            🎉 {Number(estimatedData?.coupon?.discount_percent)}% coupon
            discount applied
          </div>
        )}

        <div className="onboardingPayment__divider" />

        {/* --- Coupon Code Input — USD only --- */}
        {currency === "USD" && (
          <div className="onboardingPayment__couponSection">
            <div className="onboardingPayment__couponGroup">
              <input
                type="text"
                className="onboardingPayment__couponInput"
                placeholder="Enter coupon code"
                value={appliedCoupon ?? coupon}
                readOnly={!!appliedCoupon}
                onChange={(e) => {
                  setCoupon(e.target.value);
                  if (couponError) setCouponError("");
                }}
                disabled={disabled}
              />
              {!appliedCoupon ? (
                <button
                  className="onboardingPayment__couponBtn onboardingPayment__couponBtn--apply"
                  type="button"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || disabled || !coupon.trim()}
                >
                  {couponLoading ? "Applying..." : "Apply"}
                </button>
              ) : (
                <button
                  className="onboardingPayment__couponBtn onboardingPayment__couponBtn--remove"
                  type="button"
                  onClick={handleRemoveCoupon}
                  disabled={disabled}
                >
                  Remove
                </button>
              )}
            </div>
            {couponError && (
              <p className="onboardingPayment__couponError">{couponError}</p>
            )}
            {couponSuccess && (
              <p className="onboardingPayment__couponSuccess">
                {couponSuccess}
              </p>
            )}
          </div>
        )}

        {/* --- Security + Payment Buttons ---*/}
        <div className="onboardingPayment__footer">
          <div className="onboardingPayment__securityInfo">
            <span className="onboardingPayment__shieldIcon">🛡️</span>
            <div>
              <p className="onboardingPayment__secureText">
                100% Secure Payment
              </p>
              {currency === "INR" ? (
                <p className="onboardingPayment__poweredBy">
                  Powered by{" "}
                  <img
                    src="https://cdn.worldvectorlogo.com/logos/razorpay.svg"
                    alt="Razorpay"
                    className="onboardingPayment__providerLogo"
                  />
                </p>
              ) : (
                <p className="onboardingPayment__poweredBy">
                  Powered by Stripe & PayPal
                </p>
              )}
            </div>
          </div>

          <div className="onboardingPayment__buttons">
            {currency === "USD" ? (
              <>
                <button
                  className="onboardingPayment__payBtn onboardingPayment__payBtn--stripe"
                  type="button"
                  onClick={
                    disabled ? undefined : () => handlePaymentClick("stripe")
                  }
                  disabled={disabled}
                >
                  {processingMethod === "stripe" ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="onboardingPayment__btnLock"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="5"
                          y="11"
                          width="14"
                          height="10"
                          rx="2"
                          fill="currentColor"
                        />
                        <path
                          d="M8 11V7a4 4 0 1 1 8 0v4"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>
                      <span>Pay with Stripe</span>
                      <svg
                        className="onboardingPayment__btnArrow"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 12h14M13 6l6 6-6 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </>
                  )}
                </button>
                <button
                  className="onboardingPayment__payBtn onboardingPayment__payBtn--paypal"
                  type="button"
                  onClick={
                    disabled ? undefined : () => handlePaymentClick("paypal")
                  }
                  disabled={disabled}
                >
                  {processingMethod === "paypal" ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="onboardingPayment__btnLock"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="5"
                          y="11"
                          width="14"
                          height="10"
                          rx="2"
                          fill="currentColor"
                        />
                        <path
                          d="M8 11V7a4 4 0 1 1 8 0v4"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>
                      <span>Pay with PayPal</span>
                      <svg
                        className="onboardingPayment__btnArrow"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 12h14M13 6l6 6-6 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </>
                  )}
                </button>
              </>
            ) : currency === "INR" ? (
              <button
                className="onboardingPayment__payBtn onboardingPayment__payBtn--razorpay"
                type="button"
                onClick={
                  disabled ? undefined : () => handlePaymentClick("razorpay")
                }
                disabled={disabled}
              >
                {processingMethod === "razorpay" ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="onboardingPayment__btnLock"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="5"
                        y="11"
                        width="14"
                        height="10"
                        rx="2"
                        fill="currentColor"
                      />
                      <path
                        d="M8 11V7a4 4 0 1 1 8 0v4"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                    <span>
                      Proceed to pay {currencySymbol}
                      {Number(displayPrice).toFixed(0)}
                      {billingPeriod && (
                        <span className="onboardingPayment__btnPeriod">
                          {billingPeriod}
                        </span>
                      )}
                    </span>
                    <svg
                      className="onboardingPayment__btnArrow"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 12h14M13 6l6 6-6 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
            ) : null}
          </div>
        </div>

        {isProcessing && (
          <p className="onboardingPayment__processing">
            Processing your payment...
          </p>
        )}

        <p className="onboardingPayment__safetyNote">
          Your payment information is safe and encrypted
        </p>
      </div>
    </div>
  );
};
